import { useEffect, useRef, useState } from 'react'
import { ConfigEditor, type ConfigEditorHandle } from '@/components/config/ConfigEditor'
import { UserMultiSelect } from '@/components/config/UserMultiSelect'
import { adminApi } from '@/services/api'
import { configApi } from '@/services/api/config'
import { useAuthStore } from '@/store/authStore'

export function AdminSettingsPage() {
  const editorRef = useRef<ConfigEditorHandle>(null)

  // Konzept A — "kein Vertreter"-Empfänger (admin field-scoped)
  const [canEdit, setCanEdit] = useState(false)
  const [ids, setIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  // Konzept A — Superadmin-Schalter: dürfen Admins die Empfängerliste bearbeiten?
  const user = useAuthStore(state => state.user)
  const isSuperadmin = user?.role === 'superadmin' || user?.role === 'ultraadmin'
  const [adminsCanEdit, setAdminsCanEdit] = useState(false)
  const [permLoading, setPermLoading] = useState(false)
  const [permMsg, setPermMsg] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    adminApi
      .getNoSubstituteRecipients()
      .then(res => {
        if (!alive) return
        setCanEdit(!!res?.can_edit)
        setIds(res?.configured_ids || [])
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [])

  // Superadmin: aktuellen Wert des Berechtigungs-Schalters laden
  useEffect(() => {
    if (!isSuperadmin) return
    let alive = true
    setPermLoading(true)
    configApi
      .getConfig('substitution_request')
      .then(res => {
        if (!alive) return
        const v = (res as { config?: Record<string, unknown> })?.config?.['admins_can_edit_no_substitute_recipients']
        setAdminsCanEdit(v === true)
      })
      .catch(() => {})
      .finally(() => alive && setPermLoading(false))
    return () => {
      alive = false
    }
  }, [isSuperadmin])

  const toggleAdminsCanEdit = async (next: boolean) => {
    setAdminsCanEdit(next)
    setPermMsg(null)
    try {
      await configApi.updateConfig('substitution_request', {
        admins_can_edit_no_substitute_recipients: next,
      })
      setPermMsg(next ? 'Admins dürfen jetzt die Empfängerliste bearbeiten.' : 'Empfängerliste für Admins wieder gesperrt.')
      // Liste nach Bewilligungs-Wechsel neu laden, damit canEdit stimmt
      const res = await adminApi.getNoSubstituteRecipients()
      setCanEdit(!!res?.can_edit)
    } catch (e: unknown) {
      setAdminsCanEdit(!next)
      const err = e as { body?: { detail?: string }; message?: string }
      setPermMsg(err?.body?.detail || err?.message || 'Speichern fehlgeschlagen.')
    }
  }

  const save = async () => {
    setSaving(true)
    setMsg(null)
    try {
      await adminApi.updateNoSubstituteRecipients(ids)
      setMsg('Gespeichert.')
    } catch (e: unknown) {
      const err = e as { body?: { detail?: string }; message?: string }
      setMsg(err?.body?.detail || err?.message || 'Speichern fehlgeschlagen.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center px-6 py-4 border-b bg-white">
        <div>
          <h1 className="text-2xl font-bold">Admin-Einstellungen</h1>
          <p className="text-sm text-gray-500 mt-1">Konfiguration der Admin-Listen und Dashboard-Anzeige</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => editorRef.current?.reset()}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Zurücksetzen
          </button>
          <button
            onClick={() => editorRef.current?.save()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
          >
            Speichern
          </button>
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <h2 className="text-sm font-semibold text-gray-800">Empfänger „kein Vertreter verfügbar"</h2>
          <p className="text-xs text-gray-500 mt-1 mb-3">
            Wer benachrichtigt wird, wenn eine Lehrkraft eine Anfrage ohne Vertretungslehrer weiterleitet. Leer = alle Admins.
          </p>
          {loading ? (
            <p className="text-xs text-gray-400">Wird geladen …</p>
          ) : canEdit ? (
            <>
              <UserMultiSelect value={ids} onChange={setIds} />
              <div className="flex items-center gap-3 mt-3">
                <button
                  onClick={save}
                  disabled={saving}
                  className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Speichert …' : 'Empfänger speichern'}
                </button>
                {msg && <span className="text-xs text-gray-600">{msg}</span>}
              </div>
            </>
          ) : (
            <p className="text-xs text-gray-400">
              Nur lesbar — der Superadmin hat das Bearbeiten der Empfängerliste für Admins nicht freigegeben.
            </p>
          )}

          {isSuperadmin && (
            <div className="mt-4 pt-3 border-t border-dashed border-gray-200">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={adminsCanEdit}
                  disabled={permLoading}
                  onChange={e => toggleAdminsCanEdit(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300"
                />
                <span>
                  <span className="text-sm font-medium text-gray-800">Admins dürfen die Empfängerliste bearbeiten</span>
                  <span className="block text-xs text-gray-500">Nur für den Superadmin sichtbar. Wenn aktiviert, sehen Admins oben das Bearbeiten-Feld.</span>
                </span>
              </label>
              {permMsg && <p className="mt-1 text-xs text-gray-600">{permMsg}</p>}
            </div>
          )}
        </div>
      </div>

      <ConfigEditor
        ref={editorRef}
        configType="admin_dashboard"
        hideDelegation={true}
        apiBase="/api/v1/admin/config"
      />
    </div>
  )
}
